/**
 * FloatingAnnotationCard
 *
 * Renders a single floating card for either a comment or a track change.
 * Used by FloatingAnnotationsLayer to position cards next to referenced text.
 *
 * Google Docs-style: collapsed shows preview with replies, hover shows
 * quick actions, expanded shows full thread with inline reply box.
 */

import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { Button, Form, Spinner, Dropdown, OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  BsTrash,
  BsReply,
  BsPencil,
  BsCheckCircle,
  BsArrowCounterclockwise,
  BsStars,
  BsThreeDotsVertical,
  BsCheck,
  BsX,
} from "react-icons/bs";
import { CommentData } from "./TipTapEditor";
import { TrackChangeData } from "./TrackChangesPanel";

// ────────────────────────────────────────────
// Props
// ────────────────────────────────────────────

interface CommentCardProps {
  kind: "comment";
  comment: CommentData;
  isActive: boolean;
  readOnly?: boolean;
  currentUser?: string;
  aiEditingCommentId?: string | null;
  onCommentClick: (id: string) => void;
  onDeleteComment: (id: string) => void;
  onResolveComment?: (id: string) => void;
  onEditComment?: (id: string, newContent: string) => void;
  onReplyToComment: (id: string, content: string, author: string) => void;
  onAIEditFromComment?: (id: string) => void;
}

interface TrackChangeCardProps {
  kind: "trackChange";
  trackChange: TrackChangeData;
  isActive: boolean;
  readOnly?: boolean;
  onChangeClick: (id: string) => void;
  onAcceptChange: (id: string) => void;
  onRejectChange: (id: string) => void;
}

export type FloatingAnnotationCardProps = CommentCardProps | TrackChangeCardProps;

// ────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────

function formatDate(date: Date | string) {
  const d = typeof date === "string" ? new Date(date) : date;
  try {
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(date);
  }
}

function truncateText(text: string, maxLength = 60) {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "…";
}

// Stable pastel colors for user avatars
const AVATAR_COLORS = [
  "#4285f4", "#ea4335", "#fbbc04", "#34a853", "#ff6d01",
  "#46bdc6", "#7baaf7", "#f07b72", "#fcd04f", "#57bb8a",
  "#e8710a", "#9334e6", "#f439a0", "#24c1e0", "#5f6368",
];

function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
}

// ────────────────────────────────────────────
// Avatar component
// ────────────────────────────────────────────

const UserAvatar: React.FC<{ name: string; size?: number }> = ({ name, size = 24 }) => {
  const color = useMemo(() => getAvatarColor(name), [name]);
  const initials = useMemo(() => getInitials(name), [name]);
  return (
    <div
      className="floating-card-avatar"
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        backgroundColor: color,
        color: "#fff",
        fontSize: size * 0.45,
        fontWeight: 600,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {initials}
    </div>
  );
};

// ────────────────────────────────────────────
// Comment Card
// ────────────────────────────────────────────

const CommentCard: React.FC<CommentCardProps & { isActive: boolean }> = ({
  comment,
  isActive,
  readOnly = false,
  currentUser = "User",
  aiEditingCommentId,
  onCommentClick,
  onDeleteComment,
  onResolveComment,
  onEditComment,
  onReplyToComment,
  onAIEditFromComment,
}) => {
  const [replyingTo, setReplyingTo] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [focusReplyId, setFocusReplyId] = useState<string | null>(null);
  const repliesContainerRef = useRef<HTMLDivElement>(null);

  // Scroll to focused reply when card expands
  useEffect(() => {
    if (isActive && focusReplyId && repliesContainerRef.current) {
      const el = repliesContainerRef.current.querySelector(`[data-reply-id="${focusReplyId}"]`) as HTMLElement;
      if (el) {
        el.scrollIntoView({ block: "nearest", behavior: "smooth" });
        // Clear highlight after animation
        const timer = setTimeout(() => setFocusReplyId(null), 1500);
        return () => clearTimeout(timer);
      }
    }
  }, [isActive, focusReplyId]);

  // Clear focus when card collapses
  useEffect(() => {
    if (!isActive) setFocusReplyId(null);
  }, [isActive]);

  const handleSubmitReply = useCallback(() => {
    if (replyText.trim()) {
      onReplyToComment(comment.id, replyText.trim(), currentUser);
      setReplyText("");
      setReplyingTo(false);
    }
  }, [replyText, onReplyToComment, comment.id, currentUser]);

  const handleStartEdit = useCallback(() => {
    setEditingId(comment.id);
    setEditText(comment.content);
    setReplyingTo(false);
  }, [comment]);

  const handleSaveEdit = useCallback(() => {
    if (editText.trim() && onEditComment) {
      onEditComment(comment.id, editText.trim());
      setEditingId(null);
      setEditText("");
    }
  }, [editText, onEditComment, comment.id]);

  const handleCancelEdit = useCallback(() => {
    setEditingId(null);
    setEditText("");
  }, []);

  const replyCount = comment.replies?.length || 0;

  // Collapsed view
  if (!isActive) {
    return (
      <div
        className={`floating-card floating-card-comment ${comment.resolvedAt ? "resolved" : ""}`}
        onClick={() => onCommentClick(comment.id)}
      >
        {/* Main comment */}
        <div className="floating-card-comment-row">
          <UserAvatar name={comment.author} size={24} />
          <div className="floating-card-comment-body">
            <div className="floating-card-header">
              <strong className="floating-card-author">{comment.author}</strong>
              <span className="floating-card-date">{formatDate(comment.createdAt)}</span>
            </div>
            <p className="floating-card-content-preview">{truncateText(comment.content)}</p>
          </div>
        </div>

        {/* Reply previews */}
        {replyCount > 0 && (
          <div className="floating-card-replies-collapsed">
            {comment.replies!.map((reply) => (
              <div
                key={reply.id}
                className="floating-card-reply-row"
                onClick={(e) => {
                  e.stopPropagation();
                  setFocusReplyId(reply.id);
                  onCommentClick(comment.id);
                }}
              >
                <UserAvatar name={reply.author} size={20} />
                <div className="floating-card-reply-body">
                  <strong className="floating-card-author" style={{ fontSize: 11 }}>{reply.author}</strong>
                  <span className="floating-card-reply-text">{truncateText(reply.content, 40)}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Hover actions */}
        {!readOnly && !comment.resolvedAt && (
          <div className="floating-card-hover-actions" onClick={(e) => e.stopPropagation()}>
            {onResolveComment && (
              <button
                className="floating-card-hover-btn"
                title="Resolve"
                onClick={() => onResolveComment(comment.id)}
              >
                <BsCheckCircle size={14} />
              </button>
            )}
            {onAIEditFromComment && (
              <button
                className="floating-card-hover-btn"
                title="Apply with AI"
                onClick={() => onAIEditFromComment(comment.id)}
                disabled={aiEditingCommentId === comment.id}
              >
                {aiEditingCommentId === comment.id ? (
                  <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />
                ) : (
                  <BsStars size={14} />
                )}
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  // Expanded view
  return (
    <div
      className={`floating-card floating-card-comment active ${comment.resolvedAt ? "resolved" : ""}`}
      onClick={() => onCommentClick(comment.id)}
    >
      {/* Main comment */}
      <div className="floating-card-comment-row">
        <UserAvatar name={comment.author} size={26} />
        <div className="floating-card-comment-body">
          <div className="floating-card-header">
            <strong className="floating-card-author">{comment.author}</strong>
            <div className="floating-card-header-right">
              {comment.resolvedAt && (
                <span className="floating-card-resolved-badge">
                  <BsCheckCircle size={10} /> Resolved
                </span>
              )}
              <span className="floating-card-date">{formatDate(comment.createdAt)}</span>
            </div>
          </div>

          {/* Content or edit area */}
          {editingId === comment.id ? (
            <div className="floating-card-edit" onClick={(e) => e.stopPropagation()}>
              <Form.Control
                as="textarea"
                rows={3}
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Escape") handleCancelEdit(); }}
                className="mb-1"
                autoFocus
              />
              <div className="d-flex gap-1">
                <Button variant="primary" size="sm" onClick={handleSaveEdit} disabled={!editText.trim()}>
                  Save
                </Button>
                <Button variant="outline-secondary" size="sm" onClick={handleCancelEdit}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <p className="floating-card-content" style={{ whiteSpace: "pre-wrap" }}>
              {comment.content}
            </p>
          )}
        </div>
      </div>

      {/* Actions row */}
      {editingId !== comment.id && (
        <div className="floating-card-actions">
          {comment.resolvedAt ? (
            <>
              {onResolveComment && !readOnly && (
                <OverlayTrigger placement="top" overlay={<Tooltip>Unresolve</Tooltip>}>
                  <Button
                    variant="link" size="sm" className="p-0 me-2 text-success"
                    onClick={(e) => { e.stopPropagation(); onResolveComment(comment.id); }}
                  >
                    <BsArrowCounterclockwise size={16} />
                  </Button>
                </OverlayTrigger>
              )}
              {!readOnly && (
                <OverlayTrigger placement="top" overlay={<Tooltip>Delete</Tooltip>}>
                  <Button
                    variant="link" size="sm" className="p-0 text-danger"
                    onClick={(e) => { e.stopPropagation(); onDeleteComment(comment.id); }}
                  >
                    <BsTrash size={14} />
                  </Button>
                </OverlayTrigger>
              )}
            </>
          ) : (
            <>
              {onAIEditFromComment && !readOnly && (
                <OverlayTrigger placement="top" overlay={<Tooltip>Apply with AI</Tooltip>}>
                  <Button
                    variant="link" size="sm" className="p-0 me-2 comment-ai-edit-btn"
                    onClick={(e) => { e.stopPropagation(); onAIEditFromComment(comment.id); }}
                    disabled={aiEditingCommentId === comment.id}
                  >
                    {aiEditingCommentId === comment.id ? (
                      <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />
                    ) : (
                      <BsStars size={16} />
                    )}
                  </Button>
                </OverlayTrigger>
              )}
              {onResolveComment && !readOnly && (
                <OverlayTrigger placement="top" overlay={<Tooltip>Resolve</Tooltip>}>
                  <Button
                    variant="link" size="sm" className="p-0 me-2"
                    onClick={(e) => { e.stopPropagation(); onResolveComment(comment.id); }}
                  >
                    <BsCheckCircle size={16} />
                  </Button>
                </OverlayTrigger>
              )}
              {!readOnly && (
                <Dropdown onClick={(e) => e.stopPropagation()}>
                  <Dropdown.Toggle variant="link" size="sm" className="p-0" bsPrefix="floating-card-dropdown-toggle">
                    <BsThreeDotsVertical size={16} />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {onEditComment && (
                      <Dropdown.Item onClick={handleStartEdit} className="d-flex align-items-center">
                        <BsPencil className="me-2" size={14} /> Edit
                      </Dropdown.Item>
                    )}
                    <Dropdown.Item onClick={() => setReplyingTo(!replyingTo)} className="d-flex align-items-center">
                      <BsReply className="me-2" size={14} /> Reply
                    </Dropdown.Item>
                    <Dropdown.Item className="text-danger d-flex align-items-center" onClick={() => onDeleteComment(comment.id)}>
                      <BsTrash className="me-2" size={14} /> Delete
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </>
          )}
        </div>
      )}

      {/* Replies thread */}
      {replyCount > 0 && (
        <div className="floating-card-replies" ref={repliesContainerRef}>
          {comment.replies!.map((reply) => (
            <div key={reply.id} data-reply-id={reply.id} className={`floating-card-reply${focusReplyId === reply.id ? " focused" : ""}`}>
              <div className="floating-card-reply-row">
                <UserAvatar name={reply.author} size={22} />
                <div className="floating-card-reply-body">
                  <div className="floating-card-header">
                    <strong className="floating-card-author">{reply.author}</strong>
                    <span className="floating-card-date">{formatDate(reply.createdAt)}</span>
                  </div>
                  <p className="floating-card-content mb-0">{reply.content}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reply input — always visible when expanded (Google Docs style) */}
      {!readOnly && !comment.resolvedAt && (
        <div className="floating-card-reply-input" onClick={(e) => e.stopPropagation()}>
          {replyingTo ? (
            <>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Write a reply..."
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmitReply();
                  }
                  if (e.key === "Escape") {
                    setReplyingTo(false);
                    setReplyText("");
                  }
                }}
                autoFocus
              />
              <div className="floating-card-reply-actions">
                <Button variant="primary" size="sm" onClick={handleSubmitReply} disabled={!replyText.trim()}>
                  Reply
                </Button>
                <Button variant="link" size="sm" className="text-muted" onClick={() => { setReplyingTo(false); setReplyText(""); }}>
                  Cancel
                </Button>
              </div>
            </>
          ) : (
            <div
              className="floating-card-reply-placeholder"
              onClick={() => setReplyingTo(true)}
            >
              <UserAvatar name={currentUser} size={20} />
              <span>Reply...</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ────────────────────────────────────────────
// Track Change Card
// ────────────────────────────────────────────

const TrackChangeCard: React.FC<TrackChangeCardProps & { isActive: boolean }> = ({
  trackChange,
  isActive,
  readOnly = false,
  onChangeClick,
  onAcceptChange,
  onRejectChange,
}) => {
  // Collapsed
  if (!isActive) {
    return (
      <div
        className={`floating-card floating-card-track-change ${trackChange.type}`}
        onClick={() => onChangeClick(trackChange.id)}
      >
        <div className="floating-card-header">
          <strong className="floating-card-author">
            {trackChange.author || "Unknown"}
          </strong>
          {trackChange.date && (
            <span className="floating-card-date">{formatDate(trackChange.date)}</span>
          )}
        </div>
        <div className={`floating-card-change-text ${trackChange.type}`}>
          {trackChange.type === "insertion" ? (
            <ins>{truncateText(trackChange.text, 60)}</ins>
          ) : (
            <del>{truncateText(trackChange.text, 60)}</del>
          )}
        </div>
        {/* Hover actions */}
        {!readOnly && (
          <div className="floating-card-hover-actions" onClick={(e) => e.stopPropagation()}>
            <button
              className="floating-card-hover-btn accept"
              title="Accept"
              onClick={() => onAcceptChange(trackChange.id)}
            >
              <BsCheck size={14} />
            </button>
            <button
              className="floating-card-hover-btn reject"
              title="Reject"
              onClick={() => onRejectChange(trackChange.id)}
            >
              <BsX size={14} />
            </button>
          </div>
        )}
      </div>
    );
  }

  // Expanded
  return (
    <div
      className={`floating-card floating-card-track-change ${trackChange.type} active`}
      onClick={() => onChangeClick(trackChange.id)}
    >
      <div className="floating-card-header">
        <div className="floating-card-header-left">
          <strong className="floating-card-author">
            {trackChange.author || "Unknown"}
          </strong>
          {trackChange.date && (
            <span className="floating-card-date">{formatDate(trackChange.date)}</span>
          )}
        </div>
        {!readOnly && (
          <div className="floating-card-track-actions">
            <button
              className="floating-card-action-btn accept"
              onClick={(e) => { e.stopPropagation(); onAcceptChange(trackChange.id); }}
              title="Accept"
            >
              <BsCheck />
            </button>
            <button
              className="floating-card-action-btn reject"
              onClick={(e) => { e.stopPropagation(); onRejectChange(trackChange.id); }}
              title="Reject"
            >
              <BsX />
            </button>
          </div>
        )}
      </div>
      <div className={`floating-card-change-text ${trackChange.type}`}>
        {trackChange.type === "insertion" ? (
          <ins>{trackChange.text}</ins>
        ) : (
          <del>{trackChange.text}</del>
        )}
      </div>
    </div>
  );
};

// ────────────────────────────────────────────
// Discriminated union wrapper
// ────────────────────────────────────────────

const FloatingAnnotationCard: React.FC<FloatingAnnotationCardProps> = (props) => {
  if (props.kind === "comment") {
    return <CommentCard {...props} />;
  } else {
    return <TrackChangeCard {...props} />;
  }
};

export default FloatingAnnotationCard;
